import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Polygon, Popup, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  generateTemporalHeatmap,
  getTemporalHeatmap,
  getActivityColor,
  getActivityOpacity,
  getResolutionDescription,
  getSliceTypeLabel,
  formatDwellTime,
} from '../../../services/heatmap.service';

/**
 * TemporalHeatmap - Animated heatmap showing activity over time slices
 */
const TemporalHeatmap = ({ alienId, height = 400 }) => {
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [sliceType, setSliceType] = useState('weekly');
  const [resolution, setResolution] = useState(8);
  const [days, setDays] = useState(60);
  const [currentSliceIndex, setCurrentSliceIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const slices = heatmapData?.slices || [];
  const currentSlice = slices[currentSliceIndex] || null;

  // Calculate max score across all slices for consistent coloring
  const maxScore = useMemo(() => {
    if (!slices.length) return 1;
    let max = 0;
    slices.forEach(slice => {
      slice.cells?.forEach(cell => {
        if (cell.timeWeightedScore > max) {
          max = cell.timeWeightedScore;
        }
      });
    });
    return max || 1;
  }, [slices]);

  // Auto-play animation
  useEffect(() => {
    if (!isPlaying || slices.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSliceIndex(prev => {
        const next = prev + 1;
        if (next >= slices.length) {
          setIsPlaying(false);
          return 0;
        }
        return next;
      });
    }, 1500); // 1.5 second per slice

    return () => clearInterval(interval);
  }, [isPlaying, slices.length]);

  // Fetch or generate temporal heatmap
  const fetchHeatmap = useCallback(async (forceRegenerate = false) => {
    if (!alienId) return;

    try {
      setLoading(true);
      setError(null);
      setCurrentSliceIndex(0);
      setIsPlaying(false);

      let data = null;

      // Try cached first
      if (!forceRegenerate) {
        data = await getTemporalHeatmap(alienId, sliceType, resolution);
      }

      // Generate if needed
      if (!data) {
        setGenerating(true);
        data = await generateTemporalHeatmap(alienId, sliceType, days, resolution);
        setGenerating(false);
      }

      setHeatmapData(data);
    } catch (err) {
      console.error('Error fetching temporal heatmap:', err);
      setError('Failed to load temporal heatmap');
    } finally {
      setLoading(false);
      setGenerating(false);
    }
  }, [alienId, sliceType, resolution, days]);

  useEffect(() => {
    fetchHeatmap();
  }, [fetchHeatmap]);

  const handleRegenerateClick = () => {
    fetchHeatmap(true);
  };

  const handleSliceTypeChange = (e) => {
    setSliceType(e.target.value);
  };

  const handlePlayPause = () => {
    if (slices.length <= 1) return;
    setIsPlaying(!isPlaying);
  };

  const handleSliderChange = (e) => {
    setCurrentSliceIndex(parseInt(e.target.value, 10));
    setIsPlaying(false);
  };

  const handlePrev = () => {
    setCurrentSliceIndex(prev => Math.max(0, prev - 1));
    setIsPlaying(false);
  };

  const handleNext = () => {
    setCurrentSliceIndex(prev => Math.min(slices.length - 1, prev + 1));
    setIsPlaying(false);
  };

  if (!alienId) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Temporal Heatmap</h3>
        <div className="bg-gray-100 rounded-lg flex items-center justify-center text-gray-500" style={{ height }}>
          No participant selected
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Temporal Heatmap</h3>
        <div className="bg-gray-100 rounded-lg flex items-center justify-center" style={{ height }}>
          <div className="text-gray-500">
            {generating ? 'Generating temporal heatmap...' : 'Loading...'}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Temporal Heatmap</h3>
        <div className="bg-gray-100 rounded-lg flex items-center justify-center text-red-500" style={{ height }}>
          {error}
        </div>
      </div>
    );
  }

  // Calculate map center
  const getMapCenter = () => {
    const cells = currentSlice?.cells || slices[0]?.cells || [];
    if (cells.length > 0) {
      const topCell = cells[0];
      return [topCell.cellCenterLat, topCell.cellCenterLon];
    }
    return [39.8283, -98.5795];
  };

  const mapCenter = getMapCenter();
  const cells = currentSlice?.cells || [];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Temporal Heatmap</h3>
        <div className="flex items-center gap-3">
          {/* Slice type selector */}
          <div className="flex items-center gap-1">
            <label className="text-xs text-gray-500">View:</label>
            <select
              value={sliceType}
              onChange={handleSliceTypeChange}
              className="text-xs border border-gray-300 rounded px-1 py-0.5"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          {/* Regenerate button */}
          <button
            onClick={handleRegenerateClick}
            disabled={generating}
            className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded disabled:opacity-50"
          >
            {generating ? 'Generating...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Current slice info */}
      {currentSlice && (
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="text-sm font-medium text-gray-700">
            {currentSlice.sliceLabel}
          </div>
          <div className="text-xs text-gray-500">
            {currentSlice.totalActivity} check-ins
          </div>
        </div>
      )}

      {/* Timeline controls */}
      {slices.length > 1 && (
        <div className="mb-3 flex items-center gap-2">
          {/* Play/Pause button */}
          <button
            onClick={handlePlayPause}
            className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded"
          >
            {isPlaying ? (
              <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
            )}
          </button>

          {/* Prev button */}
          <button
            onClick={handlePrev}
            disabled={currentSliceIndex === 0}
            className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50"
          >
            <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Timeline slider */}
          <input
            type="range"
            min={0}
            max={slices.length - 1}
            value={currentSliceIndex}
            onChange={handleSliderChange}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />

          {/* Next button */}
          <button
            onClick={handleNext}
            disabled={currentSliceIndex === slices.length - 1}
            className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50"
          >
            <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Slice counter */}
          <span className="text-xs text-gray-500 min-w-[40px] text-right">
            {currentSliceIndex + 1}/{slices.length}
          </span>
        </div>
      )}

      {/* Map */}
      {cells.length > 0 || slices.length > 0 ? (
        <div className="rounded-lg overflow-hidden" style={{ height }}>
          <MapContainer
            center={mapCenter}
            zoom={resolution >= 9 ? 14 : resolution === 8 ? 12 : 10}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Fit bounds to all slices' cells */}
            <FitBoundsToAllSlices slices={slices} />

            {/* Render current slice's cells */}
            {cells.map(cell => (
              <HexCell
                key={cell.gridCellId}
                cell={cell}
                maxScore={maxScore}
              />
            ))}
          </MapContainer>
        </div>
      ) : (
        <div
          className="bg-gray-100 rounded-lg flex items-center justify-center text-gray-500"
          style={{ height }}
        >
          No location data available for the selected time range
        </div>
      )}

      {/* Legend */}
      <div className="mt-3 pt-3 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h4 className="text-xs font-semibold text-gray-600">Activity Level</h4>
            <div className="flex items-center h-3">
              <div
                className="w-20 h-full rounded"
                style={{
                  background: 'linear-gradient(to right, #3b82f6, #eab308, #ef4444)',
                }}
              />
            </div>
            <span className="text-xs text-gray-500">Low to High</span>
          </div>
          <div className="text-xs text-gray-400">
            {getSliceTypeLabel(sliceType)} view | {getResolutionDescription(resolution)}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Component to fit map bounds to all slices' cells
 * Focuses on the main activity cluster (cells near the highest-activity cell)
 */
const FitBoundsToAllSlices = ({ slices }) => {
  const map = useMap();

  useEffect(() => {
    if (!slices.length) return;

    // Collect all cells from all slices with their scores
    const allCells = [];
    slices.forEach(slice => {
      slice.cells?.forEach(cell => {
        if (cell.cellCenterLat && cell.cellCenterLon) {
          allCells.push(cell);
        }
      });
    });

    if (allCells.length === 0) return;

    // Sort cells by activity to find the primary hotspot
    const sortedCells = [...allCells].sort((a, b) => b.timeWeightedScore - a.timeWeightedScore);
    const topCell = sortedCells[0];
    const topLat = topCell.cellCenterLat;
    const topLon = topCell.cellCenterLon;

    // Filter to cells within ~100km of the top cell (roughly 1 degree lat/lon)
    const nearbyCells = allCells.filter(cell => {
      const latDiff = Math.abs(cell.cellCenterLat - topLat);
      const lonDiff = Math.abs(cell.cellCenterLon - topLon);
      return latDiff < 1 && lonDiff < 1;
    });

    // Use nearby cells if we have enough, otherwise use top 80% by score
    const cellsToFit = nearbyCells.length >= 2
      ? nearbyCells
      : sortedCells.slice(0, Math.max(2, Math.ceil(allCells.length * 0.8)));

    // Get all polygon points from filtered cells
    const allPoints = [];
    cellsToFit.forEach(cell => {
      cell.polygon?.forEach(([lat, lon]) => {
        allPoints.push([lat, lon]);
      });
    });

    if (allPoints.length > 0) {
      const lats = allPoints.map(p => p[0]);
      const lons = allPoints.map(p => p[1]);

      const bounds = [
        [Math.min(...lats), Math.min(...lons)],
        [Math.max(...lats), Math.max(...lons)],
      ];

      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [slices, map]);

  return null;
};

/**
 * Hexagonal cell component
 */
const HexCell = ({ cell, maxScore }) => {
  if (!cell.polygon || cell.polygon.length < 3) {
    return null;
  }

  const positions = cell.polygon;
  const color = getActivityColor(cell.timeWeightedScore, maxScore);
  const opacity = getActivityOpacity(cell.timeWeightedScore, maxScore);

  return (
    <Polygon
      positions={positions}
      pathOptions={{
        color: color,
        fillColor: color,
        fillOpacity: opacity,
        weight: 1,
      }}
    >
      <Tooltip direction="top" offset={[0, -10]}>
        <div className="text-xs">
          <div className="font-medium">{cell.activityCount} check-ins</div>
          <div className="text-gray-500">{cell.visitCount} visits</div>
        </div>
      </Tooltip>
      <Popup>
        <div className="p-1 min-w-[140px]">
          <h4 className="font-medium text-sm mb-2">Activity Cell</h4>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Check-ins:</span>
              <span className="font-medium">{cell.activityCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Visits:</span>
              <span className="font-medium">{cell.visitCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Dwell time:</span>
              <span className="font-medium">{formatDwellTime(cell.dwellMinutesTotal)}</span>
            </div>
          </div>
        </div>
      </Popup>
    </Polygon>
  );
};

export default TemporalHeatmap;
